import { useAuth, useUser } from '@clerk/clerk-react';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactElement,
    type ReactNode,
} from 'react';

import { useIsOfflineApp } from '@/contexts/offline-mode-context';
import { getBillingStatus, type BillingStatus } from '@/lib/api-client';

/**
 * The account's plan, as the rest of the app should read it.
 *
 * `status` says whether the numbers are known. Anything that is not `'ready'`
 * reads as unlimited: an unknown plan never blocks anyone, so a slow or failed
 * `GET /api/billing` can't lock a user out of their own work. "Limited" means
 * `activeDiagramLimit !== null` — never compare against a hardcoded number.
 */
export type PlanInfo = {
    status: 'loading' | 'ready' | 'unavailable';
    pro: boolean;
    enforced: boolean;
    activeDiagramLimit: number | null;
    checkoutUrl: string | null;
    /** Refetches the plan, e.g. after checkout or after a 402 says the cached limit is stale. */
    refresh: () => Promise<void>;
};

const noRefresh = async () => {};

/** Nothing is known, so nothing is limited. */
const UNKNOWN = {
    pro: false,
    enforced: false,
    activeDiagramLimit: null,
    checkoutUrl: null,
} as const;

const UNAVAILABLE_PLAN: PlanInfo = { status: 'unavailable', ...UNKNOWN, refresh: noRefresh };

/**
 * The offline build has no account and no plan: it behaves as Pro, with no
 * limits and no checkout. `usePlan()` returns this without fetching.
 */
export const OFFLINE_PLAN: PlanInfo = {
    status: 'ready',
    pro: true,
    enforced: false,
    activeDiagramLimit: null,
    checkoutUrl: null,
    refresh: noRefresh,
};

type PlanFields = Pick<PlanInfo, 'pro' | 'enforced' | 'activeDiagramLimit' | 'checkoutUrl'>;
type PlanState = { status: 'loading' | 'unavailable' } | { status: 'ready'; plan: PlanFields };

const LOADING: PlanState = { status: 'loading' };
const UNAVAILABLE: PlanState = { status: 'unavailable' };

const PlanContext = createContext<PlanInfo>(UNAVAILABLE_PLAN);

/**
 * Fetches `GET /api/billing` once Clerk is loaded and the user is signed in,
 * and shares the plan block with the whole app. Mount it inside the Clerk
 * provider. The offline build mounts nothing: `usePlan()` answers on its own.
 */
export function PlanProvider({
    children,
    fetchStatus = getBillingStatus,
}: {
    children: ReactNode;
    fetchStatus?: (token: string) => Promise<BillingStatus>;
}): ReactElement {
    const offline = useIsOfflineApp();
    const { getToken } = useAuth();
    const { isLoaded, isSignedIn, user } = useUser();
    const userId = user?.id ?? null;
    const active = !offline && isLoaded && !!isSignedIn;

    const [state, setState] = useState<PlanState>(LOADING);

    // Read through refs so a fresh closure from the parent (an inline
    // `fetchStatus`, a re-minted `getToken`) never triggers a refetch.
    const getTokenRef = useRef(getToken);
    getTokenRef.current = getToken;
    const fetchStatusRef = useRef(fetchStatus);
    fetchStatusRef.current = fetchStatus;
    const activeRef = useRef(active);
    activeRef.current = active;
    // Bumped by every load and by every identity change or unmount, so an
    // answer that is no longer the latest question's can't overwrite the state.
    const generationRef = useRef(0);

    const load = useCallback(async () => {
        if (!activeRef.current) return;
        const generation = ++generationRef.current;
        try {
            const token = await getTokenRef.current();
            if (!token) throw new Error('No Clerk token');
            // Destructured inside the try so a response with no plan block lands in `unavailable`.
            const { pro, enforced, activeDiagramLimit, checkoutUrl } = (await fetchStatusRef.current(token)).plan;
            if (generation === generationRef.current) {
                // A block without the field has no known limit: `undefined` would read as limited and
                // block every withSlot (`count < undefined` is never true).
                const plan = { pro, enforced, activeDiagramLimit: activeDiagramLimit ?? null, checkoutUrl };
                setState({ status: 'ready', plan });
            }
        } catch {
            if (generation === generationRef.current) setState(UNAVAILABLE);
        }
    }, []);

    useEffect(() => {
        if (offline) return;
        if (!isLoaded) {
            setState(LOADING);
            return;
        }
        if (!isSignedIn) {
            setState(UNAVAILABLE);
            return;
        }
        setState(LOADING);
        void load();
        return () => {
            generationRef.current++;
        };
    }, [offline, isLoaded, isSignedIn, userId, load]);

    const value = useMemo<PlanInfo>(() => {
        if (state.status !== 'ready') return { status: state.status, ...UNKNOWN, refresh: load };
        return { status: 'ready', ...state.plan, refresh: load };
    }, [state, load]);

    return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
}

/**
 * Whether Pro-only features (the pattern composer) are closed to this account:
 * a free plan the server enforces. Never while the plan is loading or
 * unavailable (`enforced` reads false then), and never offline.
 */
export function isFreePlan(plan: Pick<PlanInfo, 'pro' | 'enforced'>): boolean {
    return plan.enforced && !plan.pro;
}

/**
 * The current plan. Offline it is always `OFFLINE_PLAN`, provider or not;
 * without a provider (and online) it is the unavailable, unlimited value.
 */
export function usePlan(): PlanInfo {
    const offline = useIsOfflineApp();
    const value = useContext(PlanContext);
    return offline ? OFFLINE_PLAN : value;
}
